import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface ContractorPartnershipProps {
    onBack: () => void;
}

interface ContractorPartnership {
    contractorName: string;
    specialty: string[];
    serviceArea: string;
    certification: string[];
    contactPerson: string;
    email: string;
    phone: string;
    partnershipBenefits: string[];
    referralCommission: string;
    status: 'Target' | 'Initial Contact' | 'Under Discussion' | 'Agreement Sent' | 'Active Partner';
    documentsNeeded: string[];
    portfolioMinimum?: string;
    insuranceRequirements: string[];
    notes: string;
}

const ContractorPartnerships: React.FC<ContractorPartnershipProps> = ({ onBack }) => {
    const [activeTab, setActiveTab] = useState<'contractors' | 'paperwork' | 'outreach'>('contractors');
    const [selectedContractors, setSelectedContractors] = useState<number[]>([]);

    const contractorPartnerships: ContractorPartnership[] = [
        {
            contractorName: "GreenTech Retrofit Solutions",
            specialty: ["HVAC Modernization", "Building Envelope", "Energy Management Systems"],
            serviceArea: "Manhattan, Brooklyn, Queens",
            certification: ["NATE Certified", "LEED AP", "EPA Section 608"],
            contactPerson: "Michael Rodriguez",
            email: "partnerships@greentechretrofit.com",
            phone: "(212) 555-0191",
            partnershipBenefits: [
                "Priority project scheduling for referred clients",
                "Dedicated project manager assignment",
                "Volume discount pricing structure",
                "Joint marketing materials and co-branding"
            ],
            referralCommission: "3-5% of project value",
            status: "Active Partner",
            documentsNeeded: [],
            portfolioMinimum: "$500K annual project volume",
            insuranceRequirements: ["$2M General Liability", "$1M Professional Liability", "Workers Comp"],
            notes: "Excellent track record with commercial HVAC retrofits. Strong LL97 compliance expertise."
        },
        {
            contractorName: "Metro Solar & Efficiency",
            specialty: ["Solar Installation", "LED Lighting", "Window Replacement"],
            serviceArea: "All 5 Boroughs",
            certification: ["NABCEP Solar Certified", "AAMA Window Certified", "OSHA 30"],
            contactPerson: "Sarah Chen",
            email: "business.dev@metrosolar.nyc",
            phone: "(718) 555-0234",
            partnershipBenefits: [
                "Fast-track permitting assistance",
                "Free energy audit for referred projects",
                "Extended warranty programs",
                "Financing support and guidance"
            ],
            referralCommission: "2-4% of project value",
            status: "Agreement Sent",
            documentsNeeded: ["Signed Partnership Agreement", "Insurance Certificates", "License Verification"],
            portfolioMinimum: "$1M annual solar projects",
            insuranceRequirements: ["$5M General Liability", "$2M Professional Liability", "Bonding"],
            notes: "Leading solar installer with excellent customer reviews. Partnership agreement under final review."
        },
        {
            contractorName: "Urban Energy Consultants",
            specialty: ["Energy Auditing", "Commissioning", "Performance Monitoring"],
            serviceArea: "NYC Metro Area",
            certification: ["CEM Certified", "BPI Certified", "ASHRAE Member"],
            contactPerson: "David Kim",
            email: "david.kim@urbanenergy.com",
            phone: "(646) 555-0345",
            partnershipBenefits: [
                "Comprehensive energy analysis reports",
                "Post-retrofit performance verification",
                "Utility rebate application assistance",
                "Ongoing monitoring and optimization"
            ],
            referralCommission: "4-6% of consulting fees",
            status: "Under Discussion",
            documentsNeeded: ["Partnership Terms Sheet", "Service Level Agreement", "Pricing Schedule"],
            portfolioMinimum: "$300K annual consulting revenue",
            insuranceRequirements: ["$1M Professional Liability", "$500K General Liability"],
            notes: "Specialized in LL97 compliance consulting. Strong analytical capabilities and reporting."
        },
        {
            contractorName: "Empire State Mechanical",
            specialty: ["Boiler Replacement", "Steam System Optimization", "Controls Upgrade"],
            serviceArea: "Manhattan, Bronx",
            certification: ["Master Plumber License", "Boiler Operator License", "EPA Certified"],
            contactPerson: "Robert Martinez",
            email: "robert@empiremechanical.nyc",
            phone: "(212) 555-0456",
            partnershipBenefits: [
                "24/7 emergency service for partners",
                "Preventive maintenance programs",
                "Equipment financing partnerships",
                "Technical training and support"
            ],
            referralCommission: "2-3% of project value",
            status: "Initial Contact",
            documentsNeeded: ["Initial Partnership Discussion", "Capability Assessment", "Reference Check"],
            portfolioMinimum: "$750K annual mechanical projects",
            insuranceRequirements: ["$3M General Liability", "$1M Auto Liability", "Workers Comp"],
            notes: "Established mechanical contractor with strong reputation. Initial discussions very promising."
        },
        {
            contractorName: "Brooklyn Building Efficiency",
            specialty: ["Insulation", "Air Sealing", "Weatherization"],
            serviceArea: "Brooklyn, Queens, Staten Island",
            certification: ["BPI Certified", "Home Performance Certified", "Spray Foam Alliance"],
            contactPerson: "Lisa Thompson",
            email: "partnerships@brooklynefficiency.com",
            phone: "(718) 555-0567",
            partnershipBenefits: [
                "Free thermal imaging assessments",
                "Blower door testing included",
                "Moisture management expertise",
                "Comprehensive weatherization packages"
            ],
            referralCommission: "3-4% of project value",
            status: "Target",
            documentsNeeded: ["Initial Outreach", "Company Profile Review", "Capability Assessment"],
            portfolioMinimum: "$400K annual insulation projects",
            insuranceRequirements: ["$2M General Liability", "$1M Environmental Liability"],
            notes: "Target contractor with excellent building envelope expertise. Need to initiate contact."
        }
    ];

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Target': return 'bg-gray-100 text-gray-800';
            case 'Initial Contact': return 'bg-blue-100 text-blue-800';
            case 'Under Discussion': return 'bg-yellow-100 text-yellow-800';
            case 'Agreement Sent': return 'bg-orange-100 text-orange-800';
            case 'Active Partner': return 'bg-green-100 text-green-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const toggleContractorSelection = (index: number) => {
        setSelectedContractors(prev => 
            prev.includes(index) 
                ? prev.filter(i => i !== index)
                : [...prev, index]
        );
    };

    const downloadPartnershipTemplate = () => {
        alert('Contractor Partnership Agreement template download would start here');
    };

    const downloadInsuranceRequirements = () => {
        alert('Insurance Requirements document download would start here');
    };

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={onBack}
                        className="mb-4 text-blue-600 hover:text-blue-800 flex items-center"
                    >
                        ← Back to Dashboard
                    </button>
                    <h1 className="text-4xl font-bold text-gray-900 mb-2">Contractor Partnerships</h1>
                    <p className="text-xl text-gray-600 mb-4">
                        Build relationships with certified contractors for energy retrofit projects
                    </p>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-blue-800 text-sm">
                            <strong>🔧 Partnership Strategy:</strong> Partner with certified contractors across all retrofit specialties 
                            to provide comprehensive project execution. Generate 2-6% referral commissions on project values.
                        </p>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="bg-white rounded-xl shadow-lg mb-8">
                    <div className="border-b border-gray-200">
                        <nav className="flex">
                            <button
                                onClick={() => setActiveTab('contractors')}
                                className={`px-6 py-4 text-sm font-medium ${
                                    activeTab === 'contractors'
                                        ? 'border-b-2 border-blue-500 text-blue-600'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                🔧 Contractor Partners
                            </button>
                            <button
                                onClick={() => setActiveTab('paperwork')}
                                className={`px-6 py-4 text-sm font-medium ${
                                    activeTab === 'paperwork'
                                        ? 'border-b-2 border-blue-500 text-blue-600'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                📄 Documentation
                            </button>
                            <button
                                onClick={() => setActiveTab('outreach')}
                                className={`px-6 py-4 text-sm font-medium ${
                                    activeTab === 'outreach'
                                        ? 'border-b-2 border-blue-500 text-blue-600'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                📧 Outreach Tools
                            </button>
                        </nav>
                    </div>

                    <div className="p-8">
                        {/* Contractor Partners Tab */}
                        {activeTab === 'contractors' && (
                            <div>
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-2xl font-semibold text-gray-900">Contractor Network</h2>
                                    <div className="text-right">
                                        <div className="text-sm text-gray-600">Active Partnerships</div>
                                        <div className="text-2xl font-bold text-green-600">
                                            {contractorPartnerships.filter(c => c.status === 'Active Partner').length}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">Revenue-generating</div>
                                    </div>
                                </div>

                                <div className="space-y-6">
                                    {contractorPartnerships.map((contractor, index) => (
                                        <div key={index} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                                            <div className="flex justify-between items-start mb-4">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <h3 className="text-xl font-bold text-gray-900">{contractor.contractorName}</h3>
                                                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(contractor.status)}`}>
                                                            {contractor.status}
                                                        </span>
                                                    </div>
                                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                                        <div>
                                                            <span className="text-sm font-medium text-gray-600">Service Area:</span>
                                                            <p className="text-gray-900">{contractor.serviceArea}</p>
                                                        </div>
                                                        <div>
                                                            <span className="text-sm font-medium text-gray-600">Referral Commission:</span>
                                                            <p className="text-green-600 font-semibold">{contractor.referralCommission}</p>
                                                        </div>
                                                        <div>
                                                            <span className="text-sm font-medium text-gray-600">Contact:</span>
                                                            <p className="text-gray-900">{contractor.contactPerson}</p>
                                                            <p className="text-blue-600 text-sm">{contractor.email}</p>
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="mb-4">
                                                        <span className="text-sm font-medium text-gray-600">Specialties:</span>
                                                        <div className="flex flex-wrap gap-2 mt-1">
                                                            {contractor.specialty.map((spec, idx) => (
                                                                <span key={idx} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                                                                    {spec}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="mb-4">
                                                        <span className="text-sm font-medium text-gray-600">Certifications:</span>
                                                        <div className="flex flex-wrap gap-2 mt-1">
                                                            {contractor.certification.map((cert, idx) => (
                                                                <span key={idx} className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                                                                    {cert}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    <div className="bg-white rounded-lg p-3 mb-4">
                                                        <h4 className="font-medium text-gray-900 mb-2">Partnership Benefits:</h4>
                                                        <ul className="text-sm text-gray-700 space-y-1">
                                                            {contractor.partnershipBenefits.map((benefit, idx) => (
                                                                <li key={idx} className="flex items-start">
                                                                    <span className="text-green-500 mr-2">✓</span>
                                                                    {benefit}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>

                                                    {contractor.documentsNeeded.length > 0 && (
                                                        <div className="bg-yellow-50 rounded-lg p-3 mb-4">
                                                            <h4 className="font-medium text-yellow-800 mb-2">Documents Needed:</h4>
                                                            <ul className="text-sm text-yellow-700 space-y-1">
                                                                {contractor.documentsNeeded.map((doc, idx) => (
                                                                    <li key={idx} className="flex items-start">
                                                                        <span className="text-yellow-500 mr-2">•</span>
                                                                        {doc}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}

                                                    <div className="bg-gray-50 rounded-lg p-3 mb-4">
                                                        <p className="text-sm text-gray-700">{contractor.notes}</p>
                                                    </div>

                                                    <div className="flex space-x-2">
                                                        <button className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded text-sm transition-colors">
                                                            Contact
                                                        </button>
                                                        <button className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded text-sm transition-colors">
                                                            View Projects
                                                        </button>
                                                        <button className="bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded text-sm transition-colors">
                                                            Generate Referral
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Documentation Tab */}
                        {activeTab === 'paperwork' && (
                            <div>
                                <h2 className="text-2xl font-semibold text-gray-900 mb-6">Partnership Documentation</h2>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="bg-white rounded-lg p-6 border border-gray-200">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Partnership Agreement Templates</h3>
                                        <div className="space-y-4">
                                            <div className="bg-gray-50 rounded-lg p-4">
                                                <h4 className="font-medium text-gray-900 mb-2">Standard Partnership Agreement</h4>
                                                <p className="text-sm text-gray-600 mb-3">Comprehensive partnership terms including referral fees, exclusivity, and service standards</p>
                                                <button 
                                                    onClick={downloadPartnershipTemplate}
                                                    className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded text-sm transition-colors"
                                                >
                                                    Download Template
                                                </button>
                                            </div>
                                            
                                            <div className="bg-gray-50 rounded-lg p-4">
                                                <h4 className="font-medium text-gray-900 mb-2">Service Level Agreement</h4>
                                                <p className="text-sm text-gray-600 mb-3">Performance standards, response times, and quality metrics</p>
                                                <button className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded text-sm transition-colors">
                                                    Download Template
                                                </button>
                                            </div>

                                            <div className="bg-gray-50 rounded-lg p-4">
                                                <h4 className="font-medium text-gray-900 mb-2">Insurance Requirements</h4>
                                                <p className="text-sm text-gray-600 mb-3">Required insurance coverage and certificate requirements</p>
                                                <button 
                                                    onClick={downloadInsuranceRequirements}
                                                    className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded text-sm transition-colors"
                                                >
                                                    Download Requirements
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-white rounded-lg p-6 border border-gray-200">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Qualification Standards</h3>
                                        <div className="space-y-4">
                                            <div>
                                                <h4 className="font-medium text-gray-900 mb-2">Required Certifications</h4>
                                                <ul className="text-sm text-gray-700 space-y-1">
                                                    <li>• NATE Certification (HVAC contractors)</li>
                                                    <li>• NABCEP Certification (Solar contractors)</li>
                                                    <li>• BPI Certification (Energy efficiency)</li>
                                                    <li>• LEED AP (Green building expertise)</li>
                                                    <li>• EPA Section 608 (Refrigerant handling)</li>
                                                </ul>
                                            </div>
                                            
                                            <div>
                                                <h4 className="font-medium text-gray-900 mb-2">Minimum Requirements</h4>
                                                <ul className="text-sm text-gray-700 space-y-1">
                                                    <li>• $2M+ General Liability Insurance</li>
                                                    <li>• Valid NYC licensing</li>
                                                    <li>• 5+ years commercial experience</li>
                                                    <li>• Workers compensation coverage</li>
                                                    <li>• Bonding for projects &gt;$100K</li>
                                                </ul>
                                            </div>

                                            <div>
                                                <h4 className="font-medium text-gray-900 mb-2">Performance Metrics</h4>
                                                <ul className="text-sm text-gray-700 space-y-1">
                                                    <li>• On-time project completion</li>
                                                    <li>• Customer satisfaction &gt;90%</li>
                                                    <li>• Post-project warranty support</li>
                                                    <li>• LL97 compliance verification</li>
                                                    <li>• Regular progress reporting</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-8 bg-green-50 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Partnership Pipeline Tracking</h3>
                                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                                        <div>
                                            <h4 className="font-medium text-gray-700 mb-2">Active Partners</h4>
                                            <div className="text-3xl font-bold text-green-600">1</div>
                                            <p className="text-sm text-gray-600">Revenue generating</p>
                                        </div>
                                        <div>
                                            <h4 className="font-medium text-gray-700 mb-2">Agreement Sent</h4>
                                            <div className="text-3xl font-bold text-orange-600">1</div>
                                            <p className="text-sm text-gray-600">Awaiting signature</p>
                                        </div>
                                        <div>
                                            <h4 className="font-medium text-gray-700 mb-2">In Discussion</h4>
                                            <div className="text-3xl font-bold text-yellow-600">1</div>
                                            <p className="text-sm text-gray-600">Negotiating terms</p>
                                        </div>
                                        <div>
                                            <h4 className="font-medium text-gray-700 mb-2">Initial Contact</h4>
                                            <div className="text-3xl font-bold text-blue-600">1</div>
                                            <p className="text-sm text-gray-600">Early discussions</p>
                                        </div>
                                        <div>
                                            <h4 className="font-medium text-gray-700 mb-2">Target Pipeline</h4>
                                            <div className="text-3xl font-bold text-gray-600">1</div>
                                            <p className="text-sm text-gray-600">Qualified prospects</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Outreach Tools Tab */}
                        {activeTab === 'outreach' && (
                            <div>
                                <h2 className="text-2xl font-semibold text-gray-900 mb-6">Contractor Outreach Templates</h2>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                                        <h4 className="font-medium text-gray-900 mb-2">Initial Partnership Inquiry</h4>
                                        <p className="text-sm text-gray-600 mb-3">Template for first contact with potential contractor partners</p>
                                        <button className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded text-sm transition-colors">
                                            Use Template
                                        </button>
                                    </div>
                                    
                                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                                        <h4 className="font-medium text-gray-900 mb-2">Partnership Proposal</h4>
                                        <p className="text-sm text-gray-600 mb-3">Comprehensive partnership opportunity presentation</p>
                                        <button className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded text-sm transition-colors">
                                            Use Template
                                        </button>
                                    </div>
                                    
                                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                                        <h4 className="font-medium text-gray-900 mb-2">Project Referral</h4>
                                        <p className="text-sm text-gray-600 mb-3">Template for referring projects to partner contractors</p>
                                        <button className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded text-sm transition-colors">
                                            Use Template
                                        </button>
                                    </div>
                                    
                                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                                        <h4 className="font-medium text-gray-900 mb-2">Follow-up After Project</h4>
                                        <p className="text-sm text-gray-600 mb-3">Post-project feedback and relationship maintenance</p>
                                        <button className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded text-sm transition-colors">
                                            Use Template
                                        </button>
                                    </div>
                                </div>

                                <div className="mt-8 bg-blue-50 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Contractor Sourcing Strategy</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <h4 className="font-medium text-gray-900 mb-3">Target Contractor Types</h4>
                                            <ul className="text-sm text-gray-700 space-y-2">
                                                <li className="flex items-start">
                                                    <span className="text-blue-500 mr-2">•</span>
                                                    HVAC specialists with LL97 experience
                                                </li>
                                                <li className="flex items-start">
                                                    <span className="text-blue-500 mr-2">•</span>
                                                    Solar installation companies
                                                </li>
                                                <li className="flex items-start">
                                                    <span className="text-blue-500 mr-2">•</span>
                                                    Building envelope contractors
                                                </li>
                                                <li className="flex items-start">
                                                    <span className="text-blue-500 mr-2">•</span>
                                                    Energy efficiency consultants
                                                </li>
                                                <li className="flex items-start">
                                                    <span className="text-blue-500 mr-2">•</span>
                                                    Controls and automation specialists
                                                </li>
                                            </ul>
                                        </div>
                                        <div>
                                            <h4 className="font-medium text-gray-900 mb-3">Partnership Value Proposition</h4>
                                            <ul className="text-sm text-gray-700 space-y-2">
                                                <li className="flex items-start">
                                                    <span className="text-green-500 mr-2">✓</span>
                                                    Qualified lead generation from LL97 analysis
                                                </li>
                                                <li className="flex items-start">
                                                    <span className="text-green-500 mr-2">✓</span>
                                                    Pre-vetted customers with financing options
                                                </li>
                                                <li className="flex items-start">
                                                    <span className="text-green-500 mr-2">✓</span>
                                                    Technical support and compliance guidance
                                                </li>
                                                <li className="flex items-start">
                                                    <span className="text-green-500 mr-2">✓</span>
                                                    Co-marketing opportunities and referrals
                                                </li>
                                                <li className="flex items-start">
                                                    <span className="text-green-500 mr-2">✓</span>
                                                    Performance tracking and optimization
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Link
                        to="/real-estate-partnerships"
                        className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
                    >
                        <div className="text-center">
                            <div className="text-2xl mb-3">🏢</div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Real Estate Partnerships</h3>
                            <p className="text-sm text-gray-600">Property companies and agent networks</p>
                        </div>
                    </Link>
                    
                    <Link
                        to="/lender-partnerships"
                        className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
                    >
                        <div className="text-center">
                            <div className="text-2xl mb-3">💰</div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Lender Partnerships</h3>
                            <p className="text-sm text-gray-600">CPACE and traditional financing partners</p>
                        </div>
                    </Link>
                    
                    <Link
                        to="/cto-recruitment"
                        className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
                    >
                        <div className="text-center">
                            <div className="text-2xl mb-3">👨‍💻</div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">CTO Recruitment</h3>
                            <p className="text-sm text-gray-600">Technical leadership hiring</p>
                        </div>
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default ContractorPartnerships;
